import { useState } from 'react'
import { useNameGenerator } from './useNameGenerator'
import { NameGeneratorForm } from './NameGeneratorForm'
import { NameGeneratorControls } from './NameGeneratorControls'
import { NameGeneratorResults } from './NameGeneratorResults'
import { SavedNamesList } from './SavedNamesList'
import { useNameStore } from '../../../../stores/nameStore'
import { Button, Alert, AlertDescription, Progress, Checkbox, Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'

export function NameGenerator() {
  const [activeTab, setActiveTab] = useState('generate')
  const { savedNames, exportAsText, exportAsJSON, importFromJSON, clearAllNames, deleteName } = useNameStore()
  
  const {
    // State
    nameType,
    setNameType,
    customNameType,
    setCustomNameType,
    subcategory,
    setSubcategory,
    customSubcategory,
    setCustomSubcategory,
    style,
    setStyle,
    customStyle,
    setCustomStyle,
    rarity,
    setRarity,
    count,
    setCount,
    motif,
    setMotif,
    customMotif,
    setCustomMotif,
    seed,
    setSeed,
    autoSeed,
    setAutoSeed,
    usePro,
    setUsePro,
    useProLite,
    setUseProLite,
    useGemini3FlashMode,
    setUseGemini3FlashMode,
    useStreaming,
    setUseStreaming,
    worldId,
    setWorldId,
    worldContext,
    setWorldContext,
    includeString,
    setIncludeString,
    includePosition,
    setIncludePosition,
    fixedFamilyName,
    setFixedFamilyName,
    fixedGivenName,
    setFixedGivenName,
    avoidString,
    setAvoidString,
    minLength,
    setMinLength,
    maxLength,
    setMaxLength,
    rhymePattern,
    setRhymePattern,
    givenNameKanaType,
    setGivenNameKanaType,
    results,
    copiedIndex,
    lastUsedSeed,
    error,
    
    // Store values
    isApiKeySet,
    generator,
    streamGenerator,
    savedStyles,
    savedMotifs,
    savedSubcategories,
    savedNameTypes,
    
    // Store functions
    addCustomValue,
    useCustomValue,
    
    // Handlers
    handleGenerate,
    handleCopy,
    handleSaveName,
    isNameSaved,
  } = useNameGenerator()

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-2">ネームジェネレータ</h2>
        <p className="text-muted-foreground mb-4">
          キャラクター名、武器名、地名など様々な名前を生成します
        </p>
      </div>

      {!isApiKeySet() && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertDescription className="text-yellow-800">
            ⚠️ Gemini APIキーが設定されていません。ヘッダーのボタンから設定してください。
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">保存済み ({savedNames.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            {/* 基本設定 */}
            <NameGeneratorForm
              nameType={nameType}
              setNameType={setNameType}
              customNameType={customNameType}
              setCustomNameType={setCustomNameType}
              subcategory={subcategory}
              setSubcategory={setSubcategory}
              customSubcategory={customSubcategory}
              setCustomSubcategory={setCustomSubcategory}
              style={style}
              setStyle={setStyle}
              customStyle={customStyle}
              setCustomStyle={setCustomStyle}
              savedStyles={savedStyles}
              savedSubcategories={savedSubcategories}
              savedNameTypes={savedNameTypes}
              addCustomValue={addCustomValue}
              useCustomValue={useCustomValue}
              worldId={worldId}
              setWorldId={setWorldId}
              worldContext={worldContext}
              setWorldContext={setWorldContext}
            />

            {/* 詳細設定 */}
            <NameGeneratorControls
              rarity={rarity}
              setRarity={setRarity}
              count={count}
              setCount={setCount}
              motif={motif}
              setMotif={setMotif}
              customMotif={customMotif}
              setCustomMotif={setCustomMotif}
              seed={seed}
              setSeed={setSeed}
              autoSeed={autoSeed}
              setAutoSeed={setAutoSeed}
              usePro={usePro}
              setUsePro={setUsePro}
              useProLite={useProLite}
              setUseProLite={setUseProLite}
              useGemini3FlashMode={useGemini3FlashMode}
              setUseGemini3FlashMode={setUseGemini3FlashMode}
              savedMotifs={savedMotifs}
              addCustomValue={addCustomValue}
              useCustomValue={useCustomValue}
              includeString={includeString}
              setIncludeString={setIncludeString}
              includePosition={includePosition}
              setIncludePosition={setIncludePosition}
              fixedFamilyName={fixedFamilyName}
              setFixedFamilyName={setFixedFamilyName}
              fixedGivenName={fixedGivenName}
              setFixedGivenName={setFixedGivenName}
              avoidString={avoidString}
              setAvoidString={setAvoidString}
              minLength={minLength}
              setMinLength={setMinLength}
              maxLength={maxLength}
              setMaxLength={setMaxLength}
              rhymePattern={rhymePattern}
              setRhymePattern={setRhymePattern}
              givenNameKanaType={givenNameKanaType}
              setGivenNameKanaType={setGivenNameKanaType}
            />

            {/* ストリーミング設定 */}
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="useStreaming"
                checked={useStreaming}
                onCheckedChange={(checked) => setUseStreaming(checked as boolean)}
              />
              <Label htmlFor="useStreaming" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                ストリーミングを使用（リアルタイム生成・進捗表示）
              </Label>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generator.isPending || streamGenerator.isStreaming || !isApiKeySet()}
              className="btn-primary px-6 py-2 w-full md:w-auto mt-4"
              title={autoSeed ? 'ランダムシード値で生成' : `シード値: ${seed || '未設定'}`}
            >
              {generator.isPending || streamGenerator.isStreaming ? '生成中...' : '名前を生成'}
            </button>
            
            {/* エラーメッセージ */}
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          {/* ストリーミング進捗表示 */}
          {streamGenerator.isStreaming && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 生成結果 */}
          <NameGeneratorResults
            results={results}
            lastUsedSeed={lastUsedSeed}
            copiedIndex={copiedIndex}
            style={style}
            handleCopy={handleCopy}
            handleSaveName={handleSaveName}
            isNameSaved={isNameSaved}
          />
        </TabsContent>

        <TabsContent value="saved">
          <SavedNamesList
            savedNames={savedNames}
            exportAsText={exportAsText}
            exportAsJSON={exportAsJSON}
            importFromJSON={importFromJSON}
            clearAllNames={clearAllNames}
            deleteName={deleteName}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}